/**
 * @file View-model para Mustache (`cvSections`) y HTML del CV.
 * @module previewRenderer
 */

import Mustache from "mustache";
import { pickLocalized, sectionTitlesById } from "./i18n.js";

const cvInlineLabels = {
	atCompany: { en: "at", es: "en" },
};

/**
 * @param {unknown} items
 * @param {string[]} fieldIds
 * @returns {Record<string, unknown>[]}
 */
function filterListItems(items, fieldIds) {
	if (!Array.isArray(items)) return [];
	return items.filter((row) =>
		fieldIds.some((id) => (row[id] || "").trim().length > 0)
	);
}

/**
 * @param {string} sectionId
 * @returns {string} Ej.: `experience` → `hasExperience`.
 */
function listVisibilityFlagKey(sectionId) {
	return `has${sectionId.charAt(0).toUpperCase()}${sectionId.slice(1)}`;
}

/**
 * @param {Record<string, unknown>} field
 * @param {Record<string, unknown>} raw
 * @param {"en" | "es"} locale
 */
function buildFormField(field, raw, locale) {
	const id = field.id;
	const it = field.inputType || "text";
	if (it === "checkboxes") {
		const rawArr = Array.isArray(raw[id]) ? raw[id] : [];
		const labels = rawArr.map((slug) => {
			const opt = (field.options || []).find(
				(o) => String(o.value ?? o.id) === String(slug)
			);
			return opt ? pickLocalized(opt.label, locale) : String(slug);
		});
		return {
			id,
			label: pickLocalized(field.label, locale),
			inputType: "checkboxes",
			value: labels,
			hasValue: rawArr.length > 0,
			isCheckboxes: true,
			isTextarea: false,
			isTitle: false,
			isSingleLine: false,
		};
	}
	const str = typeof raw[id] === "string" ? raw[id].trim() : "";
	const isTextarea = it === "textarea";
	const isTitle = it === "title";
	return {
		id,
		label: pickLocalized(field.label, locale),
		inputType: it,
		value: str,
		hasValue: str.length > 0,
		isCheckboxes: false,
		isTextarea,
		isTitle,
		isSingleLine: !isTextarea && !isTitle,
	};
}

/**
 * @param {Record<string, unknown>} section
 * @param {Record<string, unknown>} raw
 * @param {"en" | "es"} locale
 */
function buildSectionView(section, raw, locale) {
	const sid = section.id;
	/** @type {Record<string, boolean>} */
	const idFlag = {};
	if (sid) idFlag[sid] = true;

	const base = {
		id: sid,
		type: section.type,
		title: pickLocalized(section.title, locale),
		visible: false,
		isForm: section.type === "form",
		isList: section.type === "list",
		isText: section.type === "text",
		isImage: section.type === "image",
		...idFlag,
	};

	if (section.type === "form") {
		const fields = (section.fields || []).map((f) =>
			buildFormField(f, raw, locale)
		);
		const visible = fields.some((f) => f.hasValue);
		return {
			...base,
			visible,
			fields,
		};
	}

	if (section.type === "list") {
		const itemFields = section.itemFields || [];
		const ids = itemFields.map((f) => f.id);
		const rawList = raw[sid];
		const filtered = filterListItems(rawList, ids);
		const items = filtered.map((row) => {
			/** @type {Record<string, string>} */
			const trimmed = {};
			for (const f of itemFields) {
				const k = f.id;
				trimmed[k] = String(row[k] ?? "").trim();
			}
			const fields = itemFields.map((f) => {
				const val = trimmed[f.id] ?? "";
				const inputType = f.inputType || "text";
				return {
					id: f.id,
					label: pickLocalized(f.label, locale),
					value: val,
					hasValue: val.length > 0,
					inputType,
					isTextarea: inputType === "textarea",
				};
			});
			return {
				...trimmed,
				fields,
			};
		});
		return {
			...base,
			visible: items.length > 0,
			items,
			rows: items,
		};
	}

	if (section.type === "text") {
		const k = section.fieldKey;
		const v = typeof raw[k] === "string" ? raw[k].trim() : "";
		return {
			...base,
			fieldKey: k,
			label: pickLocalized(section.title, locale),
			value: v,
			hasValue: v.length > 0,
			visible: v.length > 0,
		};
	}

	if (section.type === "image") {
		const k = section.fieldKey;
		const v =
			typeof raw[k] === "string" && raw[k].length > 0 ? raw[k] : "";
		const visible = Boolean(v && v.startsWith("data:"));
		return {
			...base,
			fieldKey: k,
			value: v,
			visible,
		};
	}

	return { ...base, visible: false };
}

/**
 * Copia campos de una sección del view-model al objeto plano del Mustache.
 * @param {Record<string, unknown>} sec
 * @param {Record<string, unknown>} out
 */
function applySectionToFlat(sec, out) {
	if (sec.type === "form" && Array.isArray(sec.fields)) {
		for (const f of sec.fields) {
			if (f.isCheckboxes) continue;
			out[f.id] = f.value;
		}
	} else if (sec.type === "list" && Array.isArray(sec.items)) {
		out[sec.id] = sec.items;
	} else if (sec.type === "text" && sec.fieldKey) {
		out[sec.fieldKey] = sec.value;
	} else if (sec.type === "image" && sec.fieldKey) {
		out[sec.fieldKey] = sec.value;
	}
}

/**
 * @param {Record<string, unknown>} raw Datos del formulario (`readData`).
 * @param {{ sections: Array<Record<string, unknown>> }} structure
 * @param {"en" | "es"} locale
 * @returns {Record<string, unknown>}
 */
export function buildViewModel(raw, structure, locale) {
	const cvSections = structure.sections.map((s) =>
		buildSectionView(s, raw, locale)
	);

	const out = { ...raw };
	for (const sec of cvSections) {
		applySectionToFlat(sec, out);
	}

	out.cvSections = cvSections;

	out.hasContactLine =
		String(out.birthDate ?? "").trim().length > 0 ||
		String(out.age ?? "").trim().length > 0 ||
		String(out.location ?? "").trim().length > 0;

	out.hasContactChannels =
		String(out.cellPhone ?? "").trim().length > 0 ||
		String(out.email ?? "").trim().length > 0;

	out.hasHeaderContact = Boolean(out.hasContactLine || out.hasContactChannels);

	const byId = sectionTitlesById(structure.sections, locale);
	out.cvLabels = {
		...byId,
		atCompany: pickLocalized(cvInlineLabels.atCompany, locale),
	};

	for (const sec of cvSections) {
		if (sec.type === "list" && sec.id) {
			out[listVisibilityFlagKey(sec.id)] = sec.visible;
		}
	}

	const th = cvSections.find((s) => s.id === "talentHub");
	out.hasTalentHub = th ? Boolean(th.visible) : false;

	return out;
}

/**
 * @param {string} templateSource Contenido del `.mustache`.
 * @param {Record<string, unknown>} data
 * @param {{ sections: Array<Record<string, unknown>> }} structure
 * @param {"en" | "es"} locale
 * @returns {string} HTML del CV (fragmento bajo `.cv-sheet`).
 */
export function renderCvHtml(templateSource, data, structure, locale) {
	const view = buildViewModel(data, structure, locale);
	return Mustache.render(templateSource, view);
}
